'use client'

import { ProductCardConsumerLayoutProvider } from 'product-card/context/ProductCardConsumerLayoutContext'
import type { ResponsiveProductCardSettings } from 'product-card/context/ProductCardConsumerLayoutContext/ProductCardConsumerLayoutContext'
import { ProductCardImagesSizesProvider } from 'product-card/context/ProductCardImagesSizesContext'
import {
	useProductListCount,
	useProductListView,
} from 'product-list/hooks/useProductList/useProductList'
import type { UseFiltersSlotItem } from 'product-list/types/Filters'
import type {
	ProductListItem,
	RenderedProductListItem,
} from 'product-list/types/ProductsList'
import { View } from 'product-list/types/View'
import { getGridImagesSizes } from 'product-list/utils/getGridImagesSizes'
import { useMemo } from 'react'
import { useDefaultProductCardSettings } from '../../hooks/useDefaultProductCardSettings/useDefaultProductCardSettings'
import { ClientProductCardItem } from '../ClientProductCardItem/ClientProductCardItem'
import type { GridItem } from '../Grid/Grid'
import { GridGroup } from '../GridGroup/GridGroup'
import { Pagination } from '../Pagination/Pagination'
import type { ProductListProps } from '../ProductList/ProductList'
import { Sticky } from '../Sticky/Sticky'

export type ProductListProductCardResponsiveSettings = Record<
	View,
	ResponsiveProductCardSettings
>

type ProductListContentProps = Pick<
	ProductListProps,
	| 'displayViewSelector'
	| 'filters'
	| 'groups'
	| 'preLoadedProductsData'
	| 'responsiveProductCardSettings'
	| 'serverProducts'
	| 'inspirationalPresentation'
	| 'onUserViewSelect'
	| 'additionalTopDistanceToSticky'
>

export const ProductListContent = (
	params: Readonly<ProductListContentProps>
) => {
	const {
		filters,
		displayViewSelector,
		groups,
		preLoadedProductsData,
		responsiveProductCardSettings,
		serverProducts,
		inspirationalPresentation,
		onUserViewSelect,
		additionalTopDistanceToSticky,
	} = params

	const { filtersData, enabled: displayFilters } = filters ?? {}

	const groupedGridItems = useMemo<GridItem[][]>(() => {
		const serverProductMap = new Map<
			ProductListItem['id'],
			RenderedProductListItem
		>(serverProducts?.map((product) => [product.id, product]))
		const priceMap = new Map<
			UseFiltersSlotItem['id'],
			UseFiltersSlotItem['price']
		>(filtersData?.catalogItemsData.map((item) => [item.id, item.price]))
		return groups.map((group) =>
			group.items.map((item) => ({
				id: item.id,
				price: priceMap.get(item.id),
				isFeatured: !!item.images.landscape,
				render: serverProductMap.get(item.id)?.render ?? (
					<ClientProductCardItem
						item={item}
						preLoadedProductsData={preLoadedProductsData}
					/>
				),
			}))
		)
	}, [
		groups,
		preLoadedProductsData,
		serverProducts,
		filtersData?.catalogItemsData,
	])

	const defaultProductCardSettings = useDefaultProductCardSettings()

	const view = useProductListView()
	const count = useProductListCount()

	return (
		<>
			<Sticky
				displayViewSelector={!!displayViewSelector}
				displayFilters={!!displayFilters}
				onUserViewSelect={onUserViewSelect}
				additionalTopDistanceToSticky={additionalTopDistanceToSticky}
			/>
			{view === View.Inspirational && inspirationalPresentation ? (
				inspirationalPresentation
			) : (
				<ProductCardConsumerLayoutProvider
					layoutView={view}
					settings={
						responsiveProductCardSettings?.[view] ??
						defaultProductCardSettings[view]
					}
				>
					<ProductCardImagesSizesProvider
						defaultImageSizes={getGridImagesSizes(view)}
					>
						<Pagination>
							<GridGroup
								groupedGridItems={groupedGridItems}
								itemLimit={count}
							/>
						</Pagination>
					</ProductCardImagesSizesProvider>
				</ProductCardConsumerLayoutProvider>
			)}
		</>
	)
}
